/**
 * Generated by orval v7.10.0 🍺
 * Do not edit manually.
 * Fintecy Investors API
 * API for managing investors, accounts, and transactions in the Fintecy platform
 * OpenAPI spec version: 1.0.0
 */
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useMutation, useQuery } from '@tanstack/react-query';

import type { Error, GetAllPartiesParams, Party, PartyInput, PartyListResponse } from '.././model';

import { customInstance } from '../../axios-instance';

/**
 * Returns a paginated list of all parties (investors, entities, trusts)
 * @summary Get all parties
 */
export const getAllParties = (params?: GetAllPartiesParams, signal?: AbortSignal) => {
  return customInstance<PartyListResponse>({ url: `/api/parties`, method: 'GET', params, signal });
};

export const getGetAllPartiesQueryKey = (params?: GetAllPartiesParams) => {
  return [`/api/parties`, ...(params ? [params] : [])] as const;
};

export const getGetAllPartiesQueryOptions = <
  TData = Awaited<ReturnType<typeof getAllParties>>,
  TError = Error,
>(
  params?: GetAllPartiesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllParties>>, TError, TData>>;
  }
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetAllPartiesQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getAllParties>>> = ({ signal }) =>
    getAllParties(params, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getAllParties>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetAllPartiesQueryResult = NonNullable<Awaited<ReturnType<typeof getAllParties>>>;
export type GetAllPartiesQueryError = Error;

export function useGetAllParties<TData = Awaited<ReturnType<typeof getAllParties>>, TError = Error>(
  params: undefined | GetAllPartiesParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllParties>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAllParties>>,
          TError,
          Awaited<ReturnType<typeof getAllParties>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetAllParties<TData = Awaited<ReturnType<typeof getAllParties>>, TError = Error>(
  params?: GetAllPartiesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllParties>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAllParties>>,
          TError,
          Awaited<ReturnType<typeof getAllParties>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetAllParties<TData = Awaited<ReturnType<typeof getAllParties>>, TError = Error>(
  params?: GetAllPartiesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllParties>>, TError, TData>>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get all parties
 */

export function useGetAllParties<TData = Awaited<ReturnType<typeof getAllParties>>, TError = Error>(
  params?: GetAllPartiesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllParties>>, TError, TData>>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetAllPartiesQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Creates a new party (investor, entity, trust)
 * @summary Create a new party
 */
export const createParty = (partyInput: PartyInput, signal?: AbortSignal) => {
  return customInstance<Party>({
    url: `/api/parties`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: partyInput,
    signal,
  });
};

export const getCreatePartyMutationOptions = <TError = Error, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createParty>>,
    TError,
    { data: PartyInput },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createParty>>,
  TError,
  { data: PartyInput },
  TContext
> => {
  const mutationKey = ['createParty'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createParty>>,
    { data: PartyInput }
  > = (props) => {
    const { data } = props ?? {};

    return createParty(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreatePartyMutationResult = NonNullable<Awaited<ReturnType<typeof createParty>>>;
export type CreatePartyMutationBody = PartyInput;
export type CreatePartyMutationError = Error;

/**
 * @summary Create a new party
 */
export const useCreateParty = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof createParty>>,
      TError,
      { data: PartyInput },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof createParty>>,
  TError,
  { data: PartyInput },
  TContext
> => {
  const mutationOptions = getCreatePartyMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Returns a single party by its UUID
 * @summary Get party by ID
 */
export const getParty = (id: string, signal?: AbortSignal) => {
  return customInstance<Party>({ url: `/api/parties/${id}`, method: 'GET', signal });
};

export const getGetPartyQueryKey = (id: string) => {
  return [`/api/parties/${id}`] as const;
};

export const getGetPartyQueryOptions = <
  TData = Awaited<ReturnType<typeof getParty>>,
  TError = Error,
>(
  id: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getParty>>, TError, TData>>;
  }
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetPartyQueryKey(id);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getParty>>> = ({ signal }) =>
    getParty(id, signal);

  return { queryKey, queryFn, enabled: !!id, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getParty>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetPartyQueryResult = NonNullable<Awaited<ReturnType<typeof getParty>>>;
export type GetPartyQueryError = Error;

export function useGetParty<TData = Awaited<ReturnType<typeof getParty>>, TError = Error>(
  id: string,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getParty>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getParty>>,
          TError,
          Awaited<ReturnType<typeof getParty>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetParty<TData = Awaited<ReturnType<typeof getParty>>, TError = Error>(
  id: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getParty>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getParty>>,
          TError,
          Awaited<ReturnType<typeof getParty>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetParty<TData = Awaited<ReturnType<typeof getParty>>, TError = Error>(
  id: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getParty>>, TError, TData>>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get party by ID
 */

export function useGetParty<TData = Awaited<ReturnType<typeof getParty>>, TError = Error>(
  id: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getParty>>, TError, TData>>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetPartyQueryOptions(id, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Updates an existing party (investor, entity, trust)
 * @summary Update party
 */
export const updateParty = (id: string, partyInput: PartyInput) => {
  return customInstance<Party>({
    url: `/api/parties/${id}`,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    data: partyInput,
  });
};

export const getUpdatePartyMutationOptions = <TError = Error, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateParty>>,
    TError,
    { id: string; data: PartyInput },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateParty>>,
  TError,
  { id: string; data: PartyInput },
  TContext
> => {
  const mutationKey = ['updateParty'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateParty>>,
    { id: string; data: PartyInput }
  > = (props) => {
    const { id, data } = props ?? {};

    return updateParty(id, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdatePartyMutationResult = NonNullable<Awaited<ReturnType<typeof updateParty>>>;
export type UpdatePartyMutationBody = PartyInput;
export type UpdatePartyMutationError = Error;

/**
 * @summary Update party
 */
export const useUpdateParty = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof updateParty>>,
      TError,
      { id: string; data: PartyInput },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof updateParty>>,
  TError,
  { id: string; data: PartyInput },
  TContext
> => {
  const mutationOptions = getUpdatePartyMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Deletes an existing party (soft delete)
 * @summary Delete party
 */
export const deleteParty = (id: string) => {
  return customInstance<void>({ url: `/api/parties/${id}`, method: 'DELETE' });
};

export const getDeletePartyMutationOptions = <TError = Error, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteParty>>,
    TError,
    { id: string },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteParty>>,
  TError,
  { id: string },
  TContext
> => {
  const mutationKey = ['deleteParty'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteParty>>, { id: string }> = (
    props
  ) => {
    const { id } = props ?? {};

    return deleteParty(id);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeletePartyMutationResult = NonNullable<Awaited<ReturnType<typeof deleteParty>>>;

export type DeletePartyMutationError = Error;

/**
 * @summary Delete party
 */
export const useDeleteParty = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof deleteParty>>,
      TError,
      { id: string },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<Awaited<ReturnType<typeof deleteParty>>, TError, { id: string }, TContext> => {
  const mutationOptions = getDeletePartyMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
